const BASE = process.env.REACT_APP_API_BASE_URL || 'http://10.40.55.112/api';

const ACCESS_KEY  = 'bhu_jwt_access';
const REFRESH_KEY = 'bhu_jwt_refresh';

/* ── Token helpers ─────────────────────────────────────── */
export const getTokens   = () => ({
  access:  localStorage.getItem(ACCESS_KEY),
  refresh: localStorage.getItem(REFRESH_KEY),
});
export const saveTokens  = (access, refresh) => {
  localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
};
export const clearTokens = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

/* ── Internal silent refresh ───────────────────────────── */
/* Only ever one refresh in flight. The booking list polls a heartbeat every 8s,
   so once the access token expires several requests hit 401 within the same
   moment. The server rotates refresh tokens and blacklists the old one, so the
   first refresh to land invalidates the token the others are still holding —
   they'd fail, clear storage, and log the admin out mid-action. Everyone waits
   on the same promise instead. */
let refreshInFlight = null;

function doRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const { refresh } = getTokens();
      if (!refresh) throw new Error('no_token');
      const res = await fetch(`${BASE}/auth/token/refresh/`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ refresh }),
      });
      if (!res.ok) { clearTokens(); throw new Error('session_expired'); }
      const data = await res.json();
      saveTokens(data.access, data.refresh);
      return data.access;
    })().finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

/* ── Core fetch wrapper ────────────────────────────────── */
async function req(path, opts = {}, useApiKey = false, _retried = false) {
  const { access } = getTokens();
  const apiKey     = process.env.REACT_APP_API_KEY || '';
  const isFormData = opts.body instanceof FormData;

  const headers = {
    /* omit Content-Type for FormData — browser sets it with the multipart boundary */
    ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(useApiKey && apiKey ? { 'X-API-Key': apiKey } : {}),
    /* Send the Bearer token on API-key calls too whenever we have one. The
       public booking form has no session and goes on the key alone, but an
       admin raising a booking from the panel does — and without their token
       the server can only file the action against the anonymous external
       server, which is how every booking ended up "Created by External
       Booking Server" in Activity History. */
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
    ...(opts.headers || {}),
  };

  const res = await fetch(`${BASE}${path}`, { ...opts, headers });

  if (res.status === 204) return null;
  /* Some endpoints (e.g. DELETE) can respond 200/202 with an empty body
     instead of 204 — res.json() throws a SyntaxError on empty text, which
     would otherwise surface as a confusing "delete failed" even though the
     request actually succeeded. Read as text first and only parse if non-empty.
     A failing proxy can also answer with an HTML page rather than JSON, so
     don't let the parse throw ahead of the token handling below. */
  const text = await res.text();
  let data = null;
  if (text) { try { data = JSON.parse(text); } catch { /* non-JSON body */ } }

  /* auto-refresh once on a rejected access token. That's normally a 401, but
     the endpoints that also accept an API key answer 403 with code
     `token_not_valid` — treat both as refreshable, or the admin gets a raw
     "Given token not valid for any token type" thrown at them mid-action. */
  const tokenRejected =
    res.status === 401 || (res.status === 403 && data?.code === 'token_not_valid');
  /* Refresh whenever we actually sent a token — including on API-key calls,
     which now carry one. Keep useApiKey on the retry or the key header is
     dropped and the retry 403s. A caller with no session (the public form)
     has no token to refresh and falls through to the error below. */
  if (tokenRejected && !_retried && access) {
    try {
      const newAccess = await doRefresh();
      return req(
        path,
        { ...opts, headers: { ...headers, Authorization: `Bearer ${newAccess}` } },
        useApiKey,
        true,
      );
    } catch {
      clearTokens();
      window.dispatchEvent(new Event('bhu:session-expired'));
      throw new Error('Session expired. Please log in again.');
    }
  }

  if (!res.ok) {
    /* DRF reports a rejected field as {field: ["message", ...]} with no `detail`.
       Stringifying that whole object into the message is how raw JSON ends up in
       front of the user, so hand the parsed body back as `fieldErrors` for forms
       to pin against their inputs, and fall back to the first message as text. */
    const fieldErrors =
      data && typeof data === 'object' && !Array.isArray(data) && !data.detail ? data : null;
    const firstFieldError = fieldErrors
      ? Object.values(fieldErrors).flat().find((m) => typeof m === 'string')
      : null;
    const err = new Error(
      data?.detail || firstFieldError
        || (data ? JSON.stringify(data) : res.statusText || `Request failed (${res.status})`),
    );
    if (data?.code) err.code = data.code;
    if (fieldErrors) err.fieldErrors = fieldErrors;
    throw err;
  }
  return data;
}

/* ── Auth ─────────────────────────────────────────────── */
export const authApi = {
  login:   (username, password) =>
    req('/auth/login/',         { method: 'POST', body: JSON.stringify({ username, password }) }, false, true),
  logout:  (refresh) =>
    req('/auth/logout/',        { method: 'POST', body: JSON.stringify({ refresh }) }),
  refresh: (refresh) =>
    req('/auth/token/refresh/', { method: 'POST', body: JSON.stringify({ refresh }) }, false, true),
};

/* ── Admin Users (SUPER_ADMIN only) ───────────────────── */
export const adminUserApi = {
  list:     ()         => req('/admin-users/'),
  get:      (id)       => req(`/admin-users/${id}/`),
  create:   (data)     => req('/admin-users/',       { method: 'POST',   body: JSON.stringify(data) }),
  update:   (id, data) => req(`/admin-users/${id}/`, { method: 'PATCH',  body: JSON.stringify(data) }),
  delete:   (id)       => req(`/admin-users/${id}/`, { method: 'DELETE' }),
  assignGh: (id, ghId) => req(`/admin-users/${id}/assign-gh/`,         { method: 'POST',   body: JSON.stringify({ guest_house_id: ghId }) }),
  removeGh: (id, ghId) => req(`/admin-users/${id}/assign-gh/${ghId}/`, { method: 'DELETE' }),
};

/* ── Guest Houses ─────────────────────────────────────── */
export const ghApi = {
  list:   ()         => req('/guest-houses/'),
  /* Every guest house the caller may see, with its rooms, room types and
     floor priorities nested — the admin dashboard's whole first paint in
     one request instead of three per guest house. See
     GuestHouseViewSet.expanded. */
  expanded: ()       => req('/guest-houses/expanded/'),
  get:    (id)       => req(`/guest-houses/${id}/`),
  create: (data)     => req('/guest-houses/', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => req(`/guest-houses/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id)       => req(`/guest-houses/${id}/`, { method: 'DELETE' }),
  occupancy: (id)    => req(`/guest-houses/${id}/occupancy/`),
  /* `start`/`end` should be naive ISO datetimes (see utils/stayWindow) — dates
     alone make the server assume 12:00-in / 10:00-out. `excludeBooking` drops
     one booking's own occupancy from the check, which is what makes extending
     a stay possible: otherwise the booking blocks its own room.
     `excludeAssignment` is the narrower, usually correct form — one booking can
     hold the same room twice, so exempting the whole booking would hide a
     sibling stay that genuinely blocks the room. */
  availableRooms: (id, start, end, { excludeBooking, excludeAssignment } = {}) =>
    req(`/guest-houses/${id}/available-rooms/?${new URLSearchParams({
      start,
      end,
      ...(excludeBooking ? { exclude_booking: excludeBooking } : {}),
      ...(excludeAssignment ? { exclude_assignment: excludeAssignment } : {}),
    })}`),
  /* Binary response — req() only handles JSON, so this fetches directly and
     hands back a blob + the server-chosen filename for the caller to save. */
  downloadOccupancyReport: async (month) => {
    const { access } = getTokens();
    const res = await fetch(`${BASE}/guest-houses/occupancy-report/?${new URLSearchParams({ month })}`, {
      headers: access ? { Authorization: `Bearer ${access}` } : {},
    });
    if (!res.ok) {
      let detail = 'Failed to download report';
      try { detail = (await res.json())?.detail || detail; } catch { /* non-JSON error body */ }
      throw new Error(detail);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] || `occupancy-report-${month}.xlsx`;
    return { blob, filename };
  },
  /* The front desk's daily sheet — every room, and the stays checking in or
     out today and tomorrow. Binary like the report above, so same raw fetch.
     `variant` picks the colour sheet or the mono one for a B/W printer; the
     server names the two files differently, so both can sit in Downloads. */
  downloadOccupancyList: async (variant = 'color') => {
    const { access } = getTokens();
    const query = variant === 'bw' ? '?variant=bw' : '';
    const res = await fetch(`${BASE}/guest-houses/occupancy-list-pdf/${query}`, {
      headers: access ? { Authorization: `Bearer ${access}` } : {},
    });
    if (!res.ok) {
      let detail = 'Failed to download occupancy list';
      try { detail = (await res.json())?.detail || detail; } catch { /* non-JSON error body */ }
      throw new Error(detail);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] || 'occupancy-list.pdf';
    return { blob, filename };
  },
};

/* ── Room Types ───────────────────────────────────────── */
export const roomTypeApi = {
  list:   (ghId)           => req(`/guest-houses/${ghId}/room-types/`),
  get:    (ghId, id)       => req(`/guest-houses/${ghId}/room-types/${id}/`),
  create: (ghId, data)     => req(`/guest-houses/${ghId}/room-types/`,      { method: 'POST',   body: JSON.stringify(data) }),
  update: (ghId, id, data) => req(`/guest-houses/${ghId}/room-types/${id}/`, { method: 'PATCH',  body: JSON.stringify(data) }),
  delete: (ghId, id)       => req(`/guest-houses/${ghId}/room-types/${id}/`, { method: 'DELETE' }),
};

/* ── Floor Priority ───────────────────────────────────── */
export const floorPriorityApi = {
  list:   (ghId)           => req(`/guest-houses/${ghId}/floor-priority/`),
  create: (ghId, data)     => req(`/guest-houses/${ghId}/floor-priority/`,      { method: 'POST',   body: JSON.stringify(data) }),
  update: (ghId, id, data) => req(`/guest-houses/${ghId}/floor-priority/${id}/`, { method: 'PATCH',  body: JSON.stringify(data) }),
  delete: (ghId, id)       => req(`/guest-houses/${ghId}/floor-priority/${id}/`, { method: 'DELETE' }),
};

/* ── Payment Responsibility Options ────────────────────── */
export const paymentResponsibilityApi = {
  list:   ()         => req('/payment-responsibility-options/'),
  create: (data)     => req('/payment-responsibility-options/', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => req(`/payment-responsibility-options/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id)       => req(`/payment-responsibility-options/${id}/`, { method: 'DELETE' }),
};

/* ── Advance Booking Limits ─────────────────────────────
   Four fixed rows (booking_type × actor_role), seeded server-side — this
   API only ever lists and updates, never adds or removes one. */
export const advanceBookingLimitApi = {
  list:   ()         => req('/advance-booking-limits/'),
  update: (id, data) => req(`/advance-booking-limits/${id}/`, { method: 'PATCH', body: JSON.stringify(data) }),
};

/* ── Rooms ────────────────────────────────────────────── */
export const roomApi = {
  list:   (ghId)           => req(`/guest-houses/${ghId}/rooms/`),
  get:    (ghId, id)       => req(`/guest-houses/${ghId}/rooms/${id}/`),
  create: (ghId, data)     => req(`/guest-houses/${ghId}/rooms/`,      { method: 'POST',   body: JSON.stringify(data) }),
  update: (ghId, id, data) => req(`/guest-houses/${ghId}/rooms/${id}/`, { method: 'PATCH',  body: JSON.stringify(data) }),
  delete: (ghId, id)       => req(`/guest-houses/${ghId}/rooms/${id}/`, { method: 'DELETE' }),
  setStatus: (ghId, id, statusValue, remark) => req(`/guest-houses/${ghId}/rooms/${id}/set-status/`, { method: 'POST', body: JSON.stringify({ status: statusValue, remark: remark || '' }) }),
};

/* ── Bookings ─────────────────────────────────────────── */
export const bookingApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req(`/bookings/${qs ? `?${qs}` : ''}`);
  },
  get:            (id)                        => req(`/bookings/${id}/`),
  create:         (body)                      => req('/bookings/', { method: 'POST', body }, true),
  confirm:        (id, roomIds, remarks, roomWindows) => req(`/bookings/${id}/confirm/`, { method: 'POST', body: JSON.stringify({ room_ids: roomIds.map(Number), remarks: remarks || '', ...(roomWindows ? { room_windows: roomWindows } : {}) }) }),
  partialConfirm: (id, roomIds, note)         => req(`/bookings/${id}/partial-confirm/`, { method: 'POST', body: JSON.stringify({ room_ids: roomIds.map(Number), note: note || '' }) }),
  reject:         (id, remarks)               => req(`/bookings/${id}/reject/`,          { method: 'POST', body: JSON.stringify({ remarks: remarks || '' }) }),
  cancel:         (id, remarks)               => req(`/bookings/${id}/cancel/`,          { method: 'POST', body: JSON.stringify({ remarks: remarks || '' }) }),
  transfer:       (id, toGhId, remarks)       => req(`/bookings/${id}/transfer/`,        { method: 'POST', body: JSON.stringify({ to_gh_id: toGhId, remarks: remarks || '' }) }),
  raiseSubRequest:(id, data)                  => req(`/bookings/${id}/sub-requests/`,    { method: 'POST', body: JSON.stringify(data) }),

  /* ── Front desk ── */
  /* Room actions identify a stay by `assignment_id`: one booking can hold the
     same room twice, so a room id no longer names a single assignment. The
     room id goes along for one release so an older admin tab keeps working —
     the server prefers the assignment id when both are present. */
  checkin:    (id, assignmentIds, noShow = false, roomIds = []) => req(`/bookings/${id}/checkin/`, { method: 'POST', body: JSON.stringify({ assignment_ids: assignmentIds.map(Number), ...(roomIds.length ? { room_ids: roomIds.map(Number) } : {}), no_show: !!noShow }) }),
  checkout:   (id, assignmentIds = [])      => req(`/bookings/${id}/checkout/`, { method: 'POST', body: JSON.stringify(assignmentIds.length ? { assignment_ids: assignmentIds.map(Number) } : {}) }),
  /* Moves the rooms' departure later or earlier; the time is optional (each room keeps its own when omitted). */
  markSeen:   (id) => req(`/bookings/${id}/mark-seen/`, { method: 'POST' }),
  extend:     (id, newDepartureDate, assignmentIds = [], newDepartureTime = '', { newArrivalDate = '', newArrivalTime = '' } = {}) => req(`/bookings/${id}/extend/`, { method: 'POST', body: JSON.stringify({ ...(newDepartureDate ? { new_departure_date: newDepartureDate } : {}), ...(newDepartureTime ? { new_departure_time: newDepartureTime } : {}), ...(newArrivalDate ? { new_arrival_date: newArrivalDate } : {}), ...(newArrivalTime ? { new_arrival_time: newArrivalTime } : {}), ...(assignmentIds.length ? { assignment_ids: assignmentIds.map(Number) } : {}) }) }),
  reschedule: (id, payload)                 => req(`/bookings/${id}/reschedule/`, { method: 'POST', body: JSON.stringify(payload) }),
  /* payload is FormData when a re-uploaded official_attachment is included
     (see BookingContext.editBookingDetails) -- req() already omits
     Content-Type for FormData so the browser sets the multipart boundary. */
  editDetails:(id, payload)                 => req(`/bookings/${id}/edit-details/`, { method: 'PATCH', body: payload instanceof FormData ? payload : JSON.stringify(payload) }),
  fieldEdit:  (id, payload)                 => req(`/bookings/${id}/field-edit/`, { method: 'PATCH', body: JSON.stringify(payload) }),
  roomFieldEdit: (id, payload)              => req(`/bookings/${id}/room-field-edit/`, { method: 'PATCH', body: JSON.stringify(payload) }),
  auditLog:   (id)                          => req(`/bookings/${id}/audit-log/`),

  /* ── Mail ──
     mailLog lists only messages the relay actually accepted; a queued-but-
     undelivered one never appears, so what the admin reads is a record of mail
     that went out. sendMail is synchronous for the same reason — the admin is
     waiting, and a failure is theirs to retry. */
  mailTemplates: (id)                       => req(`/bookings/${id}/mail-templates/`),
  mailPreview: (id, template, amount)       => req(`/bookings/${id}/mail-preview/?${new URLSearchParams({ template, ...(amount ? { amount } : {}) })}`),
  mailLog:    (id)                          => req(`/bookings/${id}/mail-log/`),
  sendMail:   (id, template, amount)        => req(`/bookings/${id}/send-mail/`, { method: 'POST', body: JSON.stringify({ template, ...(amount ? { amount } : {}) }) }),
  addRemark:  (id, text)                    => req(`/bookings/${id}/remarks/`, { method: 'POST', body: JSON.stringify({ text }) }),
  changeRoom: (id, assignmentId, newRoomId, oldRoomId) => req(`/bookings/${id}/change-room/`, { method: 'POST', body: JSON.stringify({ assignment_id: Number(assignmentId), ...(oldRoomId ? { old_room_id: Number(oldRoomId) } : {}), new_room_id: Number(newRoomId) }) }),
  removeRoom: (id, assignmentId, remarks)   => req(`/bookings/${id}/remove-room/`, { method: 'POST', body: JSON.stringify({ assignment_id: Number(assignmentId), remarks }) }),
  setRoomGuestName: (id, assignmentId, guestName) => req(`/bookings/${id}/room-guest-name/`, { method: 'POST', body: JSON.stringify({ assignment_id: Number(assignmentId), guest_name: guestName }) }),
  /* `roomWindows` staggers a room off the booking's own dates:
     { [roomId]: { expected_checkin, expected_checkout } }, either end optional. */
  addRoom:    (id, roomIds, roomWindows)    => req(`/bookings/${id}/add-room/`, { method: 'POST', body: JSON.stringify({ room_ids: roomIds.map(Number), ...(roomWindows ? { room_windows: roomWindows } : {}) }) }),
  checkedIn:  (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req(`/bookings/checked-in/${qs ? `?${qs}` : ''}`);
  },
  /* Today's and tomorrow's arrivals and departures — the night desk's one
     request. Shares the daily occupancy sheet's window rules server-side. */
  movements:  (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req(`/bookings/movements/${qs ? `?${qs}` : ''}`);
  },
  months:     ()                            => req('/bookings/months/'),
  /* Past bookings for one employee, shown under the Employee ID field while
     an admin is raising a new indent — capped server-side, not paginated. */
  employeeHistory: (employeeId) => req(`/bookings/by-employee/?employee_id=${encodeURIComponent(employeeId)}`),
  heartbeat:  (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req(`/bookings/heartbeat/${qs ? `?${qs}` : ''}`);
  },
};

/* ── Sub-Requests ─────────────────────────────────────── */
export const subRequestApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req(`/sub-requests/${qs ? `?${qs}` : ''}`);
  },
  respond: (id, status, roomIds, note) =>
    req(`/sub-requests/${id}/respond/`, {
      method: 'POST',
      body: JSON.stringify({
        status,
        response_note: note || '',
        ...(roomIds?.length ? { room_ids: roomIds } : {}),
      }),
    }),
};

/* ── Dashboard ────────────────────────────────────────── */
export const dashboardApi = {
  summary: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req(`/dashboard/${qs ? `?${qs}` : ''}`);
  },
};

/* ── Notifications ────────────────────────────────────── */
export const notificationApi = {
  upcomingCheckouts: () => req('/notifications/upcoming-checkouts/'),
};

/* ── Billing ──────────────────────────────────────────
   Every bill write sends the bill's `version`; the server refuses a stale one
   (409 BILL_CHANGED) so two admins cannot overwrite each other, and answers
   with the whole bill as it now stands. */
const jsonBody = (method, data) => ({ method, body: JSON.stringify(data || {}) });

export const billingSettingsApi = {
  get:    (ghId)       => req(`/guest-houses/${ghId}/billing-settings/`),
  update: (ghId, data) => req(`/guest-houses/${ghId}/billing-settings/`, jsonBody('PATCH', data)),
};

export const chargeCategoryApi = {
  list:   (ghId)           => req(`/guest-houses/${ghId}/charge-categories/`),
  create: (ghId, data)     => req(`/guest-houses/${ghId}/charge-categories/`, jsonBody('POST', data)),
  update: (ghId, id, data) => req(`/guest-houses/${ghId}/charge-categories/${id}/`, jsonBody('PATCH', data)),
  delete: (ghId, id)       => req(`/guest-houses/${ghId}/charge-categories/${id}/`, { method: 'DELETE' }),
};

export const chargeItemApi = {
  list:   (ghId, { activeOnly = false } = {}) =>
    req(`/guest-houses/${ghId}/charge-items/${activeOnly ? '?active=1' : ''}`),
  create: (ghId, data)     => req(`/guest-houses/${ghId}/charge-items/`, jsonBody('POST', data)),
  update: (ghId, id, data) => req(`/guest-houses/${ghId}/charge-items/${id}/`, jsonBody('PATCH', data)),
  delete: (ghId, id)       => req(`/guest-houses/${ghId}/charge-items/${id}/`, { method: 'DELETE' }),
};

export const billApi = {
  get:        (bId)                   => req(`/bookings/${bId}/bill/`),
  create:     (bId)                   => req(`/bookings/${bId}/bill/`, jsonBody('POST')),
  update:     (bId, version, data)    => req(`/bookings/${bId}/bill/`, jsonBody('PATCH', { ...data, version })),
  refresh:    (bId, version)          => req(`/bookings/${bId}/bill/refresh/`, jsonBody('POST', { version })),
  setDay:     (bId, version, day, entries) =>
    req(`/bookings/${bId}/bill/days/${day}/`, jsonBody('PUT', { version, entries })),
  addLine:    (bId, version, data)    => req(`/bookings/${bId}/bill/lines/`, jsonBody('POST', { ...data, version })),
  updateLine: (bId, version, lineId, data) =>
    req(`/bookings/${bId}/bill/lines/${lineId}/`, jsonBody('PATCH', { ...data, version })),
  deleteLine: (bId, version, lineId)  =>
    req(`/bookings/${bId}/bill/lines/${lineId}/?version=${version}`, { method: 'DELETE' }),
  retention:  (bId, version, data)    => req(`/bookings/${bId}/bill/retention/`, jsonBody('POST', { ...data, version })),
  boardingLumpSum: (bId, version, data) => req(`/bookings/${bId}/bill/boarding-lump-sum/`, jsonBody('PUT', { ...data, version })),
  finalize:   (bId, version)          => req(`/bookings/${bId}/bill/finalize/`, jsonBody('POST', { version })),
  cancel:     (bId, version, reason)  => req(`/bookings/${bId}/bill/cancel/`, jsonBody('POST', { version, reason })),
  regenerate: (bId, version, reason)  => req(`/bookings/${bId}/bill/regenerate/`, jsonBody('POST', { version, reason })),
  revisions:  (bId)                   => req(`/bookings/${bId}/bills/`),
  print:      (bId, revision)         => req(`/bookings/${bId}/bill/print/${revision ? `?revision=${revision}` : ''}`),
};

/* A binary file from the API — req() only handles JSON. Hands back a blob and
   the server-chosen filename for the caller to save. */
async function downloadFile(path, fallbackName) {
  const { access } = getTokens();
  const res = await fetch(`${BASE}${path}`, { headers: access ? { Authorization: `Bearer ${access}` } : {} });
  if (!res.ok) {
    let detail = 'Download failed';
    try { detail = (await res.json())?.detail || detail; } catch { /* non-JSON error body */ }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] || fallbackName;
  return { blob, filename };
}

export const billingReportApi = {
  downloadRegister: (month) =>
    downloadFile(`/billing-register/?${new URLSearchParams({ month })}`, `billing-register-${month}.xlsx`),
};

export const paymentApi = {
  list:   (bId)              => req(`/bookings/${bId}/payments/`),
  record: (bId, data)        => req(`/bookings/${bId}/payments/`, jsonBody('POST', data)),
  void:   (bId, pId, reason) => req(`/bookings/${bId}/payments/${pId}/void/`, jsonBody('POST', { reason })),
};
